import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import {
  createSavannaMaterials,
  disposeSavannaMaterials,
  type SavannaMaterials,
} from '../assets/MaterialLibrary';
import { collectModelDiagnostics, type ModelDiagnostics } from '../assets/ModelDiagnostics';
import { disposeObject3D } from '../utils/dispose';

export type ElephantVisualState = {
  speed?: number;
  turn?: number;
  trunkLift?: number;
  trunkCurl?: number;
  trunkReach?: number;
  trunkYaw?: number;
  spraying?: boolean;
  carrying?: boolean;
  collapsed?: boolean;
  healthRatio?: number;
  waterRatio?: number;
};

export type ElephantAssetDiagnostics = {
  status: 'loading' | 'ready' | 'error';
  mode: 'procedural' | 'imported';
  source: string;
  error: string | null;
  scale: number;
  yaw: number;
  bounds: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
    size: { x: number; y: number; z: number };
  } | null;
  clip: { name: string; duration: number; tracks: number } | null;
  trunkBones: number;
  rearLegBones: number;
  model: ModelDiagnostics | null;
};

const CARRY_TRUNK_POSE = [0.42, 0.38, 0.26, 0.16, 0.08, 0.02, 0];
const SPRAY_TRUNK_POSE = [0.6, 0.55, 0.35, 0.18, 0.08, 0, -0.02];
const REACH_TRUNK_POSE = [1, 0.9, 0.55, 0.25, 0.1, 0.05, 0];
const IMPORTED_MODEL_URL = `${import.meta.env.BASE_URL}assets/models/tembo-elephant/tembo-elephant-walk.glb`;
const IMPORTED_MODEL_HEIGHT = 3.35;
const IMPORTED_MODEL_YAW = Math.PI * 0.75;
const IMPORTED_TRUNK_BONE_NAMES = Array.from({ length: 9 }, (_, index) => `bone_${index + 6}`);
const IMPORTED_REAR_LEG_BONE_NAMES = [
  ['tripo::1_Left_Limb_0', 'tripo::1_Left_Limb_1', 'tripo::1_Left_Limb_2'],
  ['tripo::1_Right_Limb_0', 'tripo::1_Right_Limb_1', 'tripo::1_Right_Limb_2'],
] as const;
const REAR_LEG_REST_BLEND = [0.08, 0.2, 0.14] as const;
const IMPORTED_TRUNK_SOCKET_LIFT = 0.18;

export type ElephantVisualOptions = {
  materials?: SavannaMaterials;
  scale?: number;
};

export class ElephantVisual {
  readonly root = new THREE.Group();
  readonly trunkTip = new THREE.Object3D();
  readonly mouthSocket = new THREE.Object3D();
  readonly carrySocket = new THREE.Object3D();
  readonly spraySocket = new THREE.Object3D();
  readonly collisionProxy: THREE.Mesh;

  private readonly materials: SavannaMaterials;
  private readonly ownsMaterials: boolean;
  private readonly proceduralMotionRoot = new THREE.Group();
  private readonly proceduralMeshes = new Set<THREE.Mesh>();
  private readonly geometries = new Set<THREE.BufferGeometry>();
  private readonly privateMaterials = new Set<THREE.Material>();
  private readonly legJoints: THREE.Group[] = [];
  private readonly trunkJoints: THREE.Group[] = [];
  private readonly trunkRoot = new THREE.Group();
  private readonly ears: THREE.Mesh[] = [];
  private readonly eyes: THREE.Mesh[] = [];
  private readonly waterBand: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  private importedRoot: THREE.Group | null = null;
  private importedTrunkBones: THREE.Bone[] = [];
  private importedRearLegBones: THREE.Bone[][] = [];
  private importedTrunkTipBone: THREE.Bone | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private walkAction: THREE.AnimationAction | null = null;
  private walkWeight = 0;
  private assetStatus: ElephantAssetDiagnostics['status'] = 'loading';
  private activeMode: ElephantAssetDiagnostics['mode'] = 'procedural';
  private assetError: string | null = null;
  private importedScale = 0;
  private importedBounds: THREE.Box3 | null = null;
  private importedClip: ElephantAssetDiagnostics['clip'] = null;
  private importedModelDiagnostics: ModelDiagnostics | null = null;
  private readonly trunkWorldAxis = new THREE.Vector3();
  private readonly trunkWorldPosition = new THREE.Vector3();
  private readonly trunkRootWorldQuaternion = new THREE.Quaternion();
  private readonly trunkParentWorldQuaternion = new THREE.Quaternion();
  private readonly trunkWorldDelta = new THREE.Quaternion();
  private readonly trunkLocalDelta = new THREE.Quaternion();
  private readonly trunkTipWorldQuaternion = new THREE.Quaternion();
  private disposed = false;
  private healthRatio = 1;
  private waterRatio = 0;

  constructor(options: ElephantVisualOptions = {}) {
    this.materials = options.materials ?? createSavannaMaterials();
    this.ownsMaterials = !options.materials;
    this.root.name = 'elephantVisual';
    this.root.scale.setScalar(options.scale ?? 1);
    this.proceduralMotionRoot.name = 'proceduralElephantMotion';
    this.root.add(this.proceduralMotionRoot);

    this.buildBody();
    this.buildHead();
    this.buildLegs();
    this.buildTrunk();

    const collisionGeometry = this.trackGeometry(new THREE.CapsuleGeometry(1.08, 2.15, 4, 8));
    const collisionMaterial = new THREE.MeshBasicMaterial({ visible: false });
    this.privateMaterials.add(collisionMaterial);
    this.collisionProxy = new THREE.Mesh(collisionGeometry, collisionMaterial);
    this.collisionProxy.name = 'elephantCollisionProxy';
    this.collisionProxy.position.set(0, 1.55, 0.15);
    this.collisionProxy.rotation.x = Math.PI / 2;
    this.collisionProxy.visible = false;
    this.root.add(this.collisionProxy);

    const waterGeometry = this.trackGeometry(new THREE.TorusGeometry(0.31, 0.035, 6, 24));
    const waterMaterial = new THREE.MeshBasicMaterial({
      color: 0x66d6df,
      transparent: true,
      opacity: 0.86,
      depthWrite: false,
    });
    this.privateMaterials.add(waterMaterial);
    this.waterBand = new THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>(waterGeometry, waterMaterial);
    this.waterBand.name = 'trunkWaterBand';
    this.waterBand.rotation.x = Math.PI / 2;
    this.waterBand.visible = false;
    this.trunkTip.add(this.waterBand);

    void this.loadImportedModel();
  }

  update(delta: number, elapsed: number, state: ElephantVisualState = {}): void {
    const speed = THREE.MathUtils.clamp(state.speed ?? 0, 0, 8);
    const stride = THREE.MathUtils.clamp(speed / 4.5, 0, 1);
    const phase = elapsed * THREE.MathUtils.lerp(1.6, 5.2, stride);
    const gait = Math.sin(phase) * 0.42 * stride;
    const proceduralInteraction = Boolean(
      state.spraying ||
      state.carrying ||
      (state.trunkReach ?? 0) > 0.001,
    );
    const importedInteractionReady = this.importedTrunkBones.length === IMPORTED_TRUNK_BONE_NAMES.length;
    const requiresProcedural = Boolean(
      state.collapsed || (proceduralInteraction && !importedInteractionReady),
    );
    const nextMode = this.assetStatus === 'ready' && !requiresProcedural ? 'imported' : 'procedural';
    this.setActiveMode(nextMode);

    if (this.mixer && this.walkAction) {
      const targetWeight = this.activeMode === 'imported' ? stride : 0;
      this.walkWeight = THREE.MathUtils.damp(this.walkWeight, targetWeight, 8, delta);
      this.walkAction.setEffectiveWeight(this.walkWeight);
      this.walkAction.setEffectiveTimeScale(THREE.MathUtils.lerp(0.45, 1.25, stride));
      this.mixer.update(delta);
      if (this.activeMode === 'imported' && importedInteractionReady) {
        this.applyImportedTrunkPose(state, stride, phase);
        this.updateImportedTrunkSocket();
      }
    }

    this.legJoints.forEach((joint, index) => {
      const opposite = index === 0 || index === 3 ? 1 : -1;
      joint.rotation.x = gait * opposite;
    });

    this.proceduralMotionRoot.position.y = Math.abs(Math.sin(phase * 2)) * 0.035 * stride;
    this.proceduralMotionRoot.rotation.z = THREE.MathUtils.damp(
      this.proceduralMotionRoot.rotation.z,
      -(state.turn ?? 0) * 0.045,
      8,
      delta,
    );

    this.ears[0].rotation.y = 0.18 + Math.sin(elapsed * 1.9) * 0.08;
    this.ears[1].rotation.y = -0.18 - Math.sin(elapsed * 1.9 + 0.25) * 0.08;

    const lift = THREE.MathUtils.clamp(state.trunkLift ?? 0, -1, 1);
    const curl = THREE.MathUtils.clamp(state.trunkCurl ?? 0, 0, 1);
    const reach = THREE.MathUtils.clamp(state.trunkReach ?? 0, 0, 1);
    const spraying = state.spraying ? 1 : 0;
    const carrying = state.carrying ? 1 : 0;
    const actionAmount = Math.max(reach, spraying, carrying * 0.82);
    this.trunkRoot.rotation.y = THREE.MathUtils.damp(
      this.trunkRoot.rotation.y,
      THREE.MathUtils.clamp(state.trunkYaw ?? 0, -1.15, 1.15) * reach,
      12,
      delta,
    );
    this.trunkJoints.forEach((joint, index) => {
      const t = index / Math.max(1, this.trunkJoints.length - 1);
      const idle = Math.sin(elapsed * 1.35 + index * 0.65) * 0.035;
      let targetRotation = idle + lift * (0.12 + t * 0.18) + curl * t * 0.32;
      targetRotation = THREE.MathUtils.lerp(targetRotation, CARRY_TRUNK_POSE[index], carrying * 0.82);
      targetRotation = THREE.MathUtils.lerp(targetRotation, SPRAY_TRUNK_POSE[index], spraying);
      targetRotation = THREE.MathUtils.lerp(targetRotation, REACH_TRUNK_POSE[index], reach);
      joint.rotation.x = THREE.MathUtils.damp(joint.rotation.x, targetRotation, 16, delta);
      const sideSway = Math.sin(elapsed * 0.8 + index) * 0.018 * (1 - actionAmount);
      joint.rotation.z = THREE.MathUtils.damp(joint.rotation.z, sideSway, 14, delta);
    });

    this.healthRatio = THREE.MathUtils.clamp(state.healthRatio ?? this.healthRatio, 0, 1);
    this.waterRatio = THREE.MathUtils.clamp(state.waterRatio ?? this.waterRatio, 0, 1);
    this.materials.elephantSkin.color.copy(new THREE.Color(0x8b725f).lerp(new THREE.Color(0x747874), this.healthRatio));
    this.waterBand.visible = this.waterRatio > 0.03;
    this.waterBand.scale.setScalar(0.8 + this.waterRatio * 0.45);
    this.waterBand.rotation.z += delta * (state.spraying ? 5.5 : 1.2);
    this.waterBand.material.opacity = state.spraying ? 1 : 0.42 + this.waterRatio * 0.45;
    this.carrySocket.position.y = state.carrying ? 0.08 : 0;
  }

  setHealthRatio(value: number): void {
    this.healthRatio = THREE.MathUtils.clamp(value, 0, 1);
  }

  setWaterRatio(value: number): void {
    this.waterRatio = THREE.MathUtils.clamp(value, 0, 1);
  }

  diagnostics(): ModelDiagnostics {
    return collectModelDiagnostics(this.root);
  }

  assetDiagnostics(): ElephantAssetDiagnostics {
    const bounds = this.importedBounds;
    const size = bounds?.getSize(new THREE.Vector3());
    return {
      status: this.assetStatus,
      mode: this.activeMode,
      source: IMPORTED_MODEL_URL,
      error: this.assetError,
      scale: this.importedScale,
      yaw: IMPORTED_MODEL_YAW,
      bounds: bounds && size
        ? {
            min: { x: bounds.min.x, y: bounds.min.y, z: bounds.min.z },
            max: { x: bounds.max.x, y: bounds.max.y, z: bounds.max.z },
            size: { x: size.x, y: size.y, z: size.z },
          }
        : null,
      clip: this.importedClip,
      trunkBones: this.importedTrunkBones.length,
      rearLegBones: this.importedRearLegBones.flat().length,
      model: this.importedModelDiagnostics,
    };
  }

  reset(): void {
    this.walkWeight = 0;
    this.walkAction?.setEffectiveWeight(0);
    this.mixer?.setTime(0);
    this.proceduralMotionRoot.position.set(0, 0, 0);
    this.proceduralMotionRoot.rotation.set(0, 0, 0);
  }

  dispose(): void {
    this.disposed = true;
    this.walkAction?.stop();
    if (this.mixer && this.importedRoot) {
      this.mixer.stopAllAction();
      this.mixer.uncacheRoot(this.importedRoot);
    }
    if (this.importedRoot) disposeObject3D(this.importedRoot);
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.privateMaterials) material.dispose();
    if (this.ownsMaterials) disposeSavannaMaterials(this.materials);
    this.root.clear();
  }

  private async loadImportedModel(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(IMPORTED_MODEL_URL);
      if (this.disposed) {
        disposeObject3D(gltf.scene);
        return;
      }

      const wrapper = new THREE.Group();
      wrapper.name = 'importedElephantAlignment';
      wrapper.rotation.y = IMPORTED_MODEL_YAW;
      wrapper.add(gltf.scene);
      wrapper.updateWorldMatrix(true, true);

      const initialBounds = new THREE.Box3().setFromObject(wrapper);
      const initialSize = initialBounds.getSize(new THREE.Vector3());
      if (initialSize.y <= 0.001) throw new Error('Imported elephant has invalid bounds.');

      this.importedScale = IMPORTED_MODEL_HEIGHT / initialSize.y;
      wrapper.scale.setScalar(this.importedScale);
      wrapper.updateWorldMatrix(true, true);
      const groundedBounds = new THREE.Box3().setFromObject(wrapper);
      wrapper.position.y -= groundedBounds.min.y;
      wrapper.updateWorldMatrix(true, true);

      gltf.scene.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.castShadow = true;
        object.receiveShadow = true;
        object.frustumCulled = true;
      });

      const trunkBones = IMPORTED_TRUNK_BONE_NAMES
        .map((name) => gltf.scene.getObjectByName(name))
        .filter((object): object is THREE.Bone => object instanceof THREE.Bone);
      if (trunkBones.length === IMPORTED_TRUNK_BONE_NAMES.length) {
        this.importedTrunkBones = trunkBones;
        this.importedTrunkTipBone = trunkBones.at(-1) ?? null;
        this.root.add(this.trunkTip);
      }

      this.importedRearLegBones = IMPORTED_REAR_LEG_BONE_NAMES.map((chain) =>
        chain
          .map((name) => gltf.scene.getObjectByName(THREE.PropertyBinding.sanitizeNodeName(name)))
          .filter((object): object is THREE.Bone => object instanceof THREE.Bone),
      );

      const clip = gltf.animations[0]?.clone();
      if (!clip) throw new Error('Imported elephant is missing its walk animation.');
      this.removeHorizontalRootMotion(clip);
      this.softenRearLegWalk(clip, gltf.scene);

      this.importedRoot = wrapper;
      this.importedBounds = new THREE.Box3().setFromObject(wrapper);
      this.importedModelDiagnostics = collectModelDiagnostics(wrapper);
      this.importedClip = { name: clip.name || 'walk', duration: clip.duration, tracks: clip.tracks.length };
      this.mixer = new THREE.AnimationMixer(gltf.scene);
      this.walkAction = this.mixer.clipAction(clip);
      this.walkAction.setLoop(THREE.LoopRepeat, Number.POSITIVE_INFINITY);
      this.walkAction.setEffectiveWeight(0);
      this.walkAction.play();
      wrapper.visible = false;
      this.root.add(wrapper);
      this.assetStatus = 'ready';
    } catch (error) {
      this.assetStatus = 'error';
      this.assetError = error instanceof Error ? error.message : String(error);
      console.warn(`Using procedural elephant fallback: ${this.assetError}`);
    }
  }

  private applyImportedTrunkPose(
    state: ElephantVisualState,
    stride: number,
    phase: number,
  ): void {
    const reach = THREE.MathUtils.clamp(state.trunkReach ?? 0, 0, 1);
    const lift = THREE.MathUtils.clamp(state.trunkLift ?? 0, -1, 1);
    const curl = THREE.MathUtils.clamp(state.trunkCurl ?? 0, 0, 1);
    const yaw = THREE.MathUtils.clamp(state.trunkYaw ?? 0, -1.15, 1.15) * reach;
    const actionAmount = Math.max(reach, state.spraying ? 1 : 0, state.carrying ? 0.82 : 0);
    const locomotionAmount = stride * (1 - actionAmount);
    const gaitSwing = Math.sin(phase - 0.35) * 0.014 * locomotionAmount;
    const sideSwing = Math.sin(phase * 0.52 + 0.8) * 0.006 * locomotionAmount;

    this.root.getWorldQuaternion(this.trunkRootWorldQuaternion);
    if (Math.abs(yaw) > 0.0001) {
      this.trunkWorldAxis.set(0, 1, 0).applyQuaternion(this.trunkRootWorldQuaternion).normalize();
      this.applyBoneWorldRotation(this.importedTrunkBones[0], this.trunkWorldAxis, yaw * 0.58);
    }

    this.trunkWorldAxis.set(1, 0, 0).applyQuaternion(this.trunkRootWorldQuaternion).normalize();
    this.importedTrunkBones.forEach((bone, index) => {
      const t = index / Math.max(1, this.importedTrunkBones.length - 1);
      const distributedLift = (lift * 0.105 + reach * 0.145) * THREE.MathUtils.lerp(1, 0.58, t);
      const tipCurl = curl * 0.07 * t;
      const walkingSwing = gaitSwing * THREE.MathUtils.lerp(0.28, 1, t);
      this.applyBoneWorldRotation(bone, this.trunkWorldAxis, distributedLift + tipCurl + walkingSwing);
    });

    if (Math.abs(sideSwing) > 0.0001) {
      this.trunkWorldAxis.set(0, 0, 1).applyQuaternion(this.trunkRootWorldQuaternion).normalize();
      this.importedTrunkBones.forEach((bone, index) => {
        const t = index / Math.max(1, this.importedTrunkBones.length - 1);
        this.applyBoneWorldRotation(bone, this.trunkWorldAxis, sideSwing * t);
      });
    }
  }

  private applyBoneWorldRotation(bone: THREE.Bone, worldAxis: THREE.Vector3, angle: number): void {
    if (Math.abs(angle) <= 0.0001 || !bone.parent) return;
    bone.parent.getWorldQuaternion(this.trunkParentWorldQuaternion);
    this.trunkWorldDelta.setFromAxisAngle(worldAxis, angle);
    this.trunkLocalDelta
      .copy(this.trunkParentWorldQuaternion)
      .invert()
      .multiply(this.trunkWorldDelta)
      .multiply(this.trunkParentWorldQuaternion);
    bone.quaternion.premultiply(this.trunkLocalDelta).normalize();
    bone.updateMatrix();
    bone.updateWorldMatrix(false, true);
  }

  private updateImportedTrunkSocket(): void {
    const tip = this.importedTrunkTipBone;
    if (!tip) return;
    tip.getWorldPosition(this.trunkWorldPosition);
    this.root.worldToLocal(this.trunkWorldPosition);
    this.trunkWorldPosition.y += IMPORTED_TRUNK_SOCKET_LIFT;
    this.trunkTip.position.copy(this.trunkWorldPosition);

    tip.getWorldQuaternion(this.trunkTipWorldQuaternion);
    this.root.getWorldQuaternion(this.trunkRootWorldQuaternion).invert();
    this.trunkTip.quaternion
      .copy(this.trunkRootWorldQuaternion)
      .multiply(this.trunkTipWorldQuaternion)
      .normalize();
    this.trunkTip.updateMatrix();
  }

  private setActiveMode(mode: ElephantAssetDiagnostics['mode']): void {
    if (
      mode === this.activeMode &&
      (!this.importedRoot || this.importedRoot.visible === (mode === 'imported'))
    ) return;
    this.activeMode = mode;
    const showProcedural = mode === 'procedural';
    for (const mesh of this.proceduralMeshes) mesh.visible = showProcedural;
    if (this.importedRoot) this.importedRoot.visible = !showProcedural;
  }

  private removeHorizontalRootMotion(clip: THREE.AnimationClip): void {
    const rootPositionTrack = `${THREE.PropertyBinding.sanitizeNodeName('tripo::Root')}.position`;
    for (const track of clip.tracks) {
      if (!track.name.endsWith(rootPositionTrack)) continue;
      const values = track.values;
      const x0 = values[0];
      const z0 = values[2];
      for (let index = 0; index < values.length; index += 3) {
        values[index] = x0;
        values[index + 2] = z0;
      }
    }
  }

  private softenRearLegWalk(clip: THREE.AnimationClip, scene: THREE.Object3D): void {
    for (const chain of IMPORTED_REAR_LEG_BONE_NAMES) {
      for (const [boneIndex, boneName] of chain.entries()) {
        const sanitizedBoneName = THREE.PropertyBinding.sanitizeNodeName(boneName);
        const bone = scene.getObjectByName(sanitizedBoneName);
        if (!(bone instanceof THREE.Bone)) continue;
        const track = clip.tracks.find(
          (candidate): candidate is THREE.QuaternionKeyframeTrack =>
            candidate instanceof THREE.QuaternionKeyframeTrack &&
            candidate.name.endsWith(`${sanitizedBoneName}.quaternion`),
        );
        if (!track) continue;

        const restRotation = bone.quaternion.clone();
        const animatedRotation = new THREE.Quaternion();
        const blend = REAR_LEG_REST_BLEND[boneIndex] ?? REAR_LEG_REST_BLEND.at(-1) ?? 0;
        for (let offset = 0; offset < track.values.length; offset += 4) {
          animatedRotation.fromArray(track.values, offset).slerp(restRotation, blend).normalize();
          animatedRotation.toArray(track.values, offset);
        }
      }
    }
  }

  private buildBody(): void {
    const body = this.mesh(new THREE.SphereGeometry(1, 20, 14), this.materials.elephantSkin, 'body');
    body.scale.set(1.38, 1.12, 1.82);
    body.position.set(0, 2.15, 0.2);
    this.proceduralMotionRoot.add(body);

    const shoulder = this.mesh(new THREE.SphereGeometry(1, 16, 12), this.materials.elephantSkinLight, 'shoulderMass');
    shoulder.scale.set(1.22, 1.12, 0.82);
    shoulder.position.set(0, 2.22, -0.93);
    this.proceduralMotionRoot.add(shoulder);

    const back = this.mesh(new THREE.SphereGeometry(1, 14, 10), this.materials.elephantSkinDark, 'backContact');
    back.scale.set(1.02, 0.16, 1.2);
    back.position.set(0, 3.13, 0.18);
    this.proceduralMotionRoot.add(back);

    const tail = new THREE.Group();
    tail.name = 'tailJoint';
    tail.position.set(0, 2.44, 1.78);
    tail.rotation.x = -0.18;
    const tailMesh = this.mesh(new THREE.CylinderGeometry(0.055, 0.035, 1.35, 7), this.materials.elephantSkinDark, 'tail');
    tailMesh.position.y = -0.63;
    const tuft = this.mesh(new THREE.ConeGeometry(0.13, 0.34, 7), this.materials.elephantSkinDark, 'tailTuft');
    tuft.position.y = -1.3;
    tuft.rotation.x = Math.PI;
    tail.add(tailMesh, tuft);
    this.proceduralMotionRoot.add(tail);

    const contact = this.mesh(new THREE.CircleGeometry(1.5, 32), this.materials.groundContact, 'groundContact');
    contact.rotation.x = -Math.PI / 2;
    contact.scale.set(1, 1.45, 1);
    contact.position.y = 0.015;
    contact.receiveShadow = false;
    this.proceduralMotionRoot.add(contact);
  }

  private buildHead(): void {
    const head = this.mesh(new THREE.SphereGeometry(1, 18, 14), this.materials.elephantSkin, 'head');
    head.scale.set(0.88, 0.95, 0.9);
    head.position.set(0, 2.58, -1.42);
    this.proceduralMotionRoot.add(head);

    const forehead = this.mesh(new THREE.SphereGeometry(1, 14, 10), this.materials.elephantSkinLight, 'forehead');
    forehead.scale.set(0.62, 0.55, 0.32);
    forehead.position.set(0, 2.98, -2.08);
    this.proceduralMotionRoot.add(forehead);

    for (const side of [-1, 1]) {
      const ear = this.mesh(new THREE.SphereGeometry(1, 16, 12), this.materials.elephantSkinDark, side < 0 ? 'leftEar' : 'rightEar');
      ear.scale.set(0.12, 0.83, 0.72);
      ear.position.set(side * 0.78, 2.69, -1.39);
      ear.rotation.z = side * 0.09;
      this.ears.push(ear);
      this.proceduralMotionRoot.add(ear);

      const eye = this.mesh(new THREE.SphereGeometry(0.065, 10, 8), this.materials.eye, side < 0 ? 'leftEye' : 'rightEye');
      eye.position.set(side * 0.48, 2.82, -2.11);
      this.eyes.push(eye);
      this.proceduralMotionRoot.add(eye);

      const brow = this.mesh(new THREE.CapsuleGeometry(0.045, 0.25, 3, 6), this.materials.elephantSkinDark, 'brow');
      brow.position.set(side * 0.48, 2.95, -2.1);
      brow.rotation.z = side * 0.9;
      this.proceduralMotionRoot.add(brow);

      const tusk = this.mesh(new THREE.ConeGeometry(0.11, 0.92, 10), this.materials.tusk, side < 0 ? 'leftTusk' : 'rightTusk');
      tusk.position.set(side * 0.35, 2.22, -2.18);
      tusk.rotation.x = -0.43;
      tusk.rotation.z = side * 0.08;
      this.proceduralMotionRoot.add(tusk);
    }

    this.mouthSocket.name = 'mouthSocket';
    this.mouthSocket.position.set(0, 2.15, -2.17);
    this.proceduralMotionRoot.add(this.mouthSocket);
  }

  private buildLegs(): void {
    const positions: ReadonlyArray<readonly [number, number]> = [
      [-0.83, -0.85],
      [0.83, -0.85],
      [-0.83, 1.03],
      [0.83, 1.03],
    ];

    positions.forEach(([x, z], index) => {
      const joint = new THREE.Group();
      joint.name = `legJoint${index}`;
      joint.position.set(x, 1.72, z);
      const upper = this.mesh(new THREE.CylinderGeometry(0.32, 0.28, 1.25, 10), this.materials.elephantSkin, 'upperLeg');
      upper.position.y = -0.56;
      const lower = this.mesh(new THREE.CylinderGeometry(0.28, 0.25, 0.82, 10), this.materials.elephantSkinLight, 'lowerLeg');
      lower.position.y = -1.53;
      const foot = this.mesh(new THREE.SphereGeometry(0.32, 10, 7), this.materials.elephantSkinDark, 'foot');
      foot.scale.set(1.08, 0.48, 1.25);
      foot.position.set(0, -1.96, -0.06);
      joint.add(upper, lower, foot);
      this.legJoints.push(joint);
      this.proceduralMotionRoot.add(joint);
    });
  }

  private buildTrunk(): void {
    this.trunkRoot.name = 'trunkRoot';
    this.trunkRoot.position.set(0, 2.47, -2.15);
    this.trunkRoot.rotation.x = -0.12;
    this.proceduralMotionRoot.add(this.trunkRoot);

    let parent = this.trunkRoot;
    const segmentLength = 0.44;
    for (let index = 0; index < 7; index += 1) {
      const joint = new THREE.Group();
      joint.name = `trunkJoint${index}`;
      const topRadius = THREE.MathUtils.lerp(0.3, 0.13, index / 7);
      const bottomRadius = THREE.MathUtils.lerp(0.27, 0.09, index / 7);
      const segment = this.mesh(
        new THREE.CylinderGeometry(topRadius, bottomRadius, segmentLength, 10, 1),
        index < 4 ? this.materials.elephantSkin : this.materials.elephantSkinLight,
        `trunkSegment${index}`,
      );
      segment.position.y = -segmentLength * 0.5;
      joint.add(segment);
      parent.add(joint);
      this.trunkJoints.push(joint);
      parent = joint;
      const next = new THREE.Group();
      next.position.y = -segmentLength;
      joint.add(next);
      parent = next;
    }

    this.trunkTip.name = 'trunkTip';
    this.trunkTip.position.y = -0.06;
    parent.add(this.trunkTip);
    this.carrySocket.name = 'carrySocket';
    this.carrySocket.position.set(0, -0.08, -0.12);
    this.trunkTip.add(this.carrySocket);
    this.spraySocket.name = 'spraySocket';
    this.spraySocket.position.set(0, -0.08, -0.22);
    this.spraySocket.rotation.x = -Math.PI / 2;
    this.trunkTip.add(this.spraySocket);
  }

  private mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
    this.trackGeometry(geometry);
    const mesh = new THREE.Mesh(geometry, material);
    this.proceduralMeshes.add(mesh);
    mesh.name = name;
    mesh.castShadow = material !== this.materials.groundContact;
    mesh.receiveShadow = material !== this.materials.groundContact;
    return mesh;
  }

  private trackGeometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.add(geometry);
    return geometry;
  }
}

export function createElephantVisual(options: ElephantVisualOptions = {}): ElephantVisual {
  return new ElephantVisual(options);
}
